import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Mail,
  Phone,
  ShieldCheck,
  Award,
  Hash,
  Briefcase
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from '../../styles/MasterView.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import UserDrawer from './UserDrawer';
import NotFound from '../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/${id}`);
      if (response.data.success) {
        setUserData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Deactivate Account?',
      text: `Are you sure you want to delete the account for ${userData.name}? This action is irreversible.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete account'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/users/${userData.id}`);
          if (response.data.success) {
            toast.success('User account deleted successfully');
            navigate('/users');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting user');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Securing Access Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>System User Details</h1>
              <p>Manage portal access, role permissions, and security configurations</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/users')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Account</span>
              </button>
              {/* <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444' }}>
                <Trash2 size={18} />
              </button> */}
            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <User size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{userData.name}</h1>
                  <span className={`${styles['status-badge']} ${userData.status === 1 ? styles['active'] : styles['inactive']}`}>
                    <ShieldCheck size={14} /> {userData.status === 1 ? 'ACCESS GRANTED' : 'ACCESS RESTRICTED'}
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Award size={16} />
                    <span>Role: <strong>{userData.roles?.[0]?.name || 'System User'}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Mail size={16} />
                    <span>Login: <strong>{userData.email}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Created: <strong>{new Date(userData.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          {/* <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Recent Logins</label>
                <h2>18</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Security Score</label>
                <h2>High</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Actions Performed</label>
                <h2>142</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>MFA Status</label>
                <h2>Enabled</h2>
              </div>
            </div>
          </div> */}

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Access Configuration */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Access Configuration</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Assigned System Role</span>
                      <span className={styles['d-value']}><Award size={16} style={{ color: '#f97316' }} />{userData.roles?.[0]?.name || 'Not Assigned'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Official Email Address</span>
                      <span className={styles['d-value']}><Mail size={16} style={{ color: '#3b82f6' }} />{userData.email}</span>
                    </div>
                  </div>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Contact Reference</span>
                      <span className={styles['d-value']}><Phone size={16} style={{ color: '#10b981' }} />{userData.mobile || 'No Phone Registered'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>User ID Tag</span>
                      <span className={styles['d-value']}>#USR-{userData.id.toString().padStart(4, '0')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Associated Profile if Employee */}
              {userData.employee && (
                <div className={styles['standard-card']}>
                  <div className={styles['card-header']}>
                    <div className={styles['card-icon']}><Briefcase size={20} /></div>
                    <h3>Associated Personnel Profile</h3>
                  </div>
                  <div className={styles['details-list']}>
                    <div className={styles['detail-row']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['d-label']}>Employee Identity Code</span>
                        <span className={styles['d-value']}><Hash size={16} style={{ color: '#f97316' }} />{userData.employee.employee_code}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['d-label']}>Professional Designation</span>
                        <span className={styles['d-value']}>{userData.employee.designation?.designation_name || 'Staff Member'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Activity */}
              {/* <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Security Audit Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>Historical login logs and sensitive action tracking for this account will be listed here.</p>
                </div>
              </div> */}
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                      <label>ACCOUNT CREATOR</label>
                      <span>{userData.creator?.name || 'System Administrator'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                      <label>ONBOARDED ON</label>
                      <span>{new Date(userData.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <UserDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        user={userData}
        onSave={fetchUser}
      />
    </div>
  );
};

export default ViewUser;
